/**
 * B2 registration lifecycle labels and login copy.
 * Mirror: backend/src/config/registrationState.ts
 */
import { APPROVAL_STATUS, type ApprovalStatusValue } from './approvalStatus';
import { OPERATIONAL_ACCESS_ERROR, OPERATIONAL_ACCESS_USER_MESSAGE } from './operationalAccess';

export { APPROVAL_STATUS, type ApprovalStatusValue };
export { OPERATIONAL_ACCESS_ERROR, OPERATIONAL_ACCESS_USER_MESSAGE };

export const REGISTRATION_LIFECYCLE = {
  PENDING_APPROVAL: 'pending_approval',
  AWAITING_ONBOARDING: 'awaiting_onboarding',
  OPERATIONAL: 'operational',
  REJECTED: 'rejected',
} as const;

export type RegistrationLifecycle =
  (typeof REGISTRATION_LIFECYCLE)[keyof typeof REGISTRATION_LIFECYCLE];

export const REGISTRATION_LIFECYCLE_DISPLAY: Record<RegistrationLifecycle, string> = {
  [REGISTRATION_LIFECYCLE.PENDING_APPROVAL]: 'Pending approval',
  [REGISTRATION_LIFECYCLE.AWAITING_ONBOARDING]: 'Awaiting onboarding',
  [REGISTRATION_LIFECYCLE.OPERATIONAL]: 'Operational',
  [REGISTRATION_LIFECYCLE.REJECTED]: 'Rejected',
};

export const APPROVAL_STATUS_LOGIN_MESSAGE: Record<ApprovalStatusValue, string> = {
  [APPROVAL_STATUS.PENDING]:
    'Your account is pending approval. You will receive an email notification once approved.',
  [APPROVAL_STATUS.APPROVED]: '',
  [APPROVAL_STATUS.REJECTED]:
    'Your account has been rejected. Please contact support for more information.',
};

export function getOperationalAccessUserMessage(
  code: (typeof OPERATIONAL_ACCESS_ERROR)[keyof typeof OPERATIONAL_ACCESS_ERROR]
): string {
  return OPERATIONAL_ACCESS_USER_MESSAGE[code];
}

export function isRegistrationOnboardingIncomplete(profile: {
  onboardingComplete?: boolean | null;
  onboardingCompleted?: boolean | null;
} | null | undefined): boolean {
  if (!profile) return false;
  if (profile.onboardingComplete === false) return true;
  if (profile.onboardingCompleted === false) return true;
  return false;
}

export function resolveRegistrationLifecycle(input: {
  approvalStatus?: string | null;
  onboardingComplete?: boolean | null;
  onboardingCompleted?: boolean | null;
}): RegistrationLifecycle {
  const status = String(input.approvalStatus || APPROVAL_STATUS.PENDING).toLowerCase();
  if (status === APPROVAL_STATUS.REJECTED) {
    return REGISTRATION_LIFECYCLE.REJECTED;
  }
  if (status === APPROVAL_STATUS.PENDING) {
    return REGISTRATION_LIFECYCLE.PENDING_APPROVAL;
  }
  if (
    status === APPROVAL_STATUS.APPROVED &&
    isRegistrationOnboardingIncomplete(input)
  ) {
    return REGISTRATION_LIFECYCLE.AWAITING_ONBOARDING;
  }
  return REGISTRATION_LIFECYCLE.OPERATIONAL;
}

export function getRegistrationLifecycleDisplay(lifecycle: RegistrationLifecycle): string {
  return REGISTRATION_LIFECYCLE_DISPLAY[lifecycle];
}

export function getRegistrationLifecycleColor(lifecycle: RegistrationLifecycle): string {
  switch (lifecycle) {
    case REGISTRATION_LIFECYCLE.REJECTED:
      return '#dc3545';
    case REGISTRATION_LIFECYCLE.PENDING_APPROVAL:
      return '#ffc107';
    case REGISTRATION_LIFECYCLE.AWAITING_ONBOARDING:
      return '#fd7e14';
    default:
      return '#28a745';
  }
}
